import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, RefreshCw, Loader2, ChevronLeft, ChevronRight, Wallet } from "lucide-react";
import { adminAxios, relativeTime } from "./adminApi";

export default function AdminLedger() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1, reasons: [] });
  const [q, setQ] = useState("");
  const [reason, setReason] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAxios().get("/ledger", { params: { q: q || undefined, reason: reason || undefined, page, page_size: 50 } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load ledger"); }
    finally { setLoading(false); }
  }, [q, reason, page]);

  useEffect(() => { load(); }, [load]);

  const totalPositive = data.items.reduce((a, i) => a + (i.delta > 0 ? i.delta : 0), 0);
  const totalNegative = data.items.reduce((a, i) => a + (i.delta < 0 ? Math.abs(i.delta) : 0), 0);

  return (
    <div data-testid="admin-ledger">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-lm-secondary">Ledger</div>
        <h1 className="mt-1 text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">Credit ledger</h1>
        <p className="text-sm text-lm-text2 mt-1">Global feed of every scan credit transaction across all workspaces. {data.total} total.</p>
      </div>

      <div className="bg-lm-surface border border-lm-border rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-lm-text3" />
          <input
            data-testid="ledger-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
            placeholder="Search by workspace or owner email…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-lm-border bg-lm-bg text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20"
          />
        </div>
        <select
          data-testid="ledger-reason-filter"
          value={reason}
          onChange={(e) => { setReason(e.target.value); setPage(1); }}
          className="text-sm border border-lm-border rounded-md px-3 py-2 bg-lm-bg"
        >
          <option value="">All reasons</option>
          {(data.reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={() => (setPage(1), load())} data-testid="ledger-refresh" className="inline-flex items-center gap-1.5 text-xs bg-lm-tint text-lm-text2 border border-lm-border rounded-md px-3 py-2 hover:bg-lm-tint2">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg border border-lm-border bg-lm-surface p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Total txns (page)</div>
          <div className="text-lg font-display font-semibold mt-0.5">{data.items.length}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Scans added (page)</div>
          <div className="text-lg font-display font-semibold mt-0.5 text-emerald-800">+{totalPositive}</div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-red-700">Scans spent (page)</div>
          <div className="text-lg font-display font-semibold mt-0.5 text-red-700">−{totalNegative}</div>
        </div>
        <div className="rounded-lg border border-lm-border bg-lm-surface p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Reasons</div>
          <div className="text-lg font-display font-semibold mt-0.5">{(data.reasons || []).length}</div>
        </div>
      </div>

      <div className="bg-lm-surface border border-lm-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-lm-tint/60 border-b border-lm-border text-lm-text3">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">When</th>
                <th className="text-left px-4 py-2.5 font-medium">Workspace</th>
                <th className="text-left px-4 py-2.5 font-medium">Owner</th>
                <th className="text-left px-4 py-2.5 font-medium">Reason</th>
                <th className="text-right px-4 py-2.5 font-medium">Δ</th>
                <th className="text-right px-4 py-2.5 font-medium">Balance</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-lm-text2"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading ledger…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-lm-text3"><Wallet className="w-6 h-6 inline mb-2 text-lm-text3" /><div>No transactions match.</div></td></tr>
              ) : data.items.map((tx) => (
                <tr key={tx.tx_id} data-testid={`ledger-row-${tx.tx_id}`} className="border-t border-lm-border/60 hover:bg-lm-tint/30">
                  <td className="px-4 py-2 font-mono text-xs text-lm-text3">
                    {tx.created_at ? new Date(tx.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-sm font-medium truncate max-w-[200px]">{tx.workspace_name || "—"}</div>
                    <div className="text-[10px] font-mono text-lm-text3 truncate max-w-[200px]">{tx.workspace_id}</div>
                  </td>
                  <td className="px-4 py-2 text-sm text-lm-text2 truncate max-w-[220px]">{tx.owner_email || "—"}</td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-lm-tint text-lm-text2 border border-lm-border">
                      {tx.reason || "—"}
                    </span>
                  </td>
                  <td className={`px-4 py-2 text-right font-mono font-semibold ${tx.delta > 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {tx.delta > 0 ? "+" : ""}{tx.delta}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-lm-text">{tx.balance_after ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-lm-border text-xs text-lm-text3 font-mono">
          <div>Page {data.page} of {data.total_pages} · {data.total} txns</div>
          <div className="flex items-center gap-1">
            <button
              data-testid="ledger-prev"
              disabled={data.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-40"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="ledger-next"
              disabled={data.page >= data.total_pages}
              onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-40"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
